import { useState } from 'react';
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Paper,
  Link as MuiLink,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Link, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { ROUTES, APP_NAME } from '../config/constants';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { EmailVerificationDialog } from '../components/auth/EmailVerificationDialog';
import { SEO } from '../components/SEO';

export const Register = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [showVerificationDialog, setShowVerificationDialog] = useState(false);
  const { signUp, isLoading } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation('auth');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError(t('passwordsDoNotMatch'));
      return;
    }

    if (password.length < 6) {
      setError(t('passwordTooShort'));
      return;
    }

    const result = await signUp(email, password, displayName);
    if (result.success) {
      // Show email verification dialog instead of navigating immediately
      setShowVerificationDialog(true);
    }
  };

  const handleVerificationDialogClose = () => {
    setShowVerificationDialog(false);
    // Navigate to login page after closing the dialog
    navigate(ROUTES.LOGIN);
  };

  return (
    <>
      <SEO 
        title="Create Account"
        description="Create your free Scribelate account and start learning Spanish, English, or Japanese through authentic podcast content."
        keywords="scribelate signup, create account, register, language learning app, free trial"
      />
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }}
      >
        <Container maxWidth="sm">
        <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
          <Typography variant="h4" component="h1" gutterBottom textAlign="center" fontWeight="bold">
            {APP_NAME}
          </Typography>
          <Typography variant="h6" gutterBottom textAlign="center" color="text.secondary" mb={4}>
            {t('createAccount')}
          </Typography>

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label={t('displayName')}
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              margin="normal"
              required
              autoFocus
            />

            <TextField
              fullWidth
              label={t('email')}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              margin="normal"
              required
              autoComplete="email"
            />

            <TextField
              fullWidth
              label={t('password')}
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              required
              autoComplete="new-password"
              helperText={t('passwordMinLength')}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label={t('confirmPassword')}
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              margin="normal"
              required
              autoComplete="new-password"
              error={!!error}
              helperText={error}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={isLoading}
              sx={{ mt: 3, mb: 2 }}
            >
              {isLoading ? t('creatingAccount') : t('signUp')}
            </Button>

            <Box textAlign="center" mt={2}>
              <Typography variant="body2" color="text.secondary">
                {t('alreadyHaveAccount')}{' '}
                <MuiLink component={Link} to={ROUTES.LOGIN} underline="hover">
                  {t('login')}
                </MuiLink>
              </Typography>
            </Box>

            <Box textAlign="center" mt={3}>
              <MuiLink component={Link} to={ROUTES.HOME} variant="body2" underline="hover">
                {t('backToHome')}
              </MuiLink>
            </Box>

            <Box textAlign="center" mt={3}>
              <Typography variant="body2" color="text.secondary">
                <MuiLink component={Link} to={ROUTES.HELP} underline="hover" sx={{ mr: 2 }}>
                  {t('footer.help')}
                </MuiLink>
                <MuiLink component={Link} to={ROUTES.PRIVACY} underline="hover" sx={{ mr: 2 }}>
                  {t('footer.privacy')}
                </MuiLink>
                <MuiLink component={Link} to={ROUTES.TERMS} underline="hover" sx={{ mr: 2 }}>
                  {t('footer.terms')}
                </MuiLink>
                <MuiLink component={Link} to={ROUTES.DMCA} underline="hover">
                  {t('footer.dmca')}
                </MuiLink>
              </Typography>
            </Box>
          </form>
        </Paper>
      </Container>

      <EmailVerificationDialog 
        open={showVerificationDialog}
        email={email}
        onClose={handleVerificationDialogClose}
      />
    </Box>
    </>
  );
};
